import React, { FC, useMemo, useState } from 'react';
import stylesModule from './Filters.module.less';
import { Button, Checkbox, Divider, Input, Space } from 'antd';
import { useCachedData } from '../../../../connection/CacheContext';
import Utils from '../../../../utils/Utils';
import { SearchQuery, useSearchContext } from '../../SearchContext';
import { FilterTitle } from './FilterTitle';

const styles: any = stylesModule;

type Props = {
  search?: boolean;
  allText?: string;
  value?: string[];
  title: string;
  aggrName: string | string[];
  onChange?: (value?: string[]) => void;
  enumName: string | string[];
  getQuery?: (value?: string[]) => SearchQuery;
  onCancel?: () => void;
  id?: string;
};

export const MasterDataFilter: FC<Props> = (props) => {
  const { onChange, enumName, search: showSearch, aggrName } = props;
  const [searchText, setSearchText] = useState<string>();
  const ctx = useSearchContext();
  const [value, setValue] = useState<string[] | undefined>(props.value);
  const { activeEnumValue } = useCachedData();
  const allData = useMemo(() => {
    const enumArr = Array.isArray(enumName) ? enumName : [enumName];
    const aggArr = Array.isArray(aggrName) ? aggrName : [aggrName];
    const st = new Set(
      enumArr.flatMap((en, i) =>
        (Object.entries(activeEnumValue[en]) ?? [])
          .map((v) => [v, (ctx.results?.aggs as any)?.[aggArr[i] as string]?.[v[0]] ?? 0])
          .map((v) => [v[0], v[1] === 0 ? '' : ` (${v[1]})`])
          .map((v, a) => ({
            label: `${v[0][1]}${v[1]}`,
            value: enumArr.length > 1 ? `${en}-${v[0][0]}` : v[0][0],
          })),
      ),
    );
    return Array.from(st);
  }, [aggrName, ctx.results?.aggs, enumName, activeEnumValue]);

  const dataToShow = useMemo(() => {
    if (showSearch && (searchText ?? '') === '') {
      return allData.filter((s) => (props.value ? props.value?.indexOf(s.value) === -1 : true));
    }
    return allData.filter((s) =>
      searchText ? s.label.toLowerCase().indexOf(searchText?.toLowerCase()) !== -1 : true,
    );
  }, [allData, props.value, searchText, showSearch]);

  const staticDataToShow = useMemo(() => {
    return allData.filter(
      (s) =>
        showSearch &&
        props.value &&
        props.value?.indexOf(s.value) !== -1 &&
        (searchText ?? '') === '',
    );
  }, [allData, props.value, searchText, showSearch]);

  return (
    <Space size={16} direction="vertical" className={styles.filterArea}>
      <FilterTitle
        id={props.id ?? 'master-data-filter'}
        title={props.title}
        onApply={() => onChange?.(value && value.length > 0 ? value : undefined)}
        onCancel={props.onCancel}
      />
      {showSearch && (
        <Input
          placeholder={'Search'}
          allowClear
          value={searchText}
          onChange={(event) => setSearchText(event.target.value)}
        />
      )}
      <div
        className={Utils.mergeClasses({
          [styles.filterAreaSearch]: showSearch ?? false,
        })}>
        <Space direction="vertical">
          {props.allText && (searchText ?? '') === '' && (
            <>
              <Checkbox
                key={'all'}
                value={'all'}
                checked={!value}
                onChange={(e) => {
                  if (e.target.checked) {
                    setValue(undefined);
                  }
                }}>
                {props.allText}
              </Checkbox>
              <Divider style={{ margin: 0 }} />
            </>
          )}
          <Space direction="vertical">
            {staticDataToShow.length > 0 && (
              <>
                {staticDataToShow.map((val) => (
                  <Checkbox
                    onChange={(e) =>
                      setValue(
                        e.target.checked
                          ? [...(value ?? []), val.value]
                          : (value ?? []).filter((v) => v !== val.value),
                      )
                    }
                    checked={(value ?? []).indexOf(val.value) !== -1}
                    key={val.value}
                    value={val.value}>
                    {val.label}
                  </Checkbox>
                ))}
                <Divider style={{ margin: 0 }} />
              </>
            )}
            {dataToShow.map((val) => (
              <Checkbox
                onChange={(e) =>
                  setValue(
                    e.target.checked
                      ? [...(value ?? []), val.value]
                      : (value ?? []).filter((v) => v !== val.value),
                  )
                }
                checked={(value ?? []).indexOf(val.value) !== -1}
                key={val.value}
                value={val.value}>
                {val.label}
              </Checkbox>
            ))}
          </Space>
        </Space>
      </div>
      <Button
        className={styles.applyButton}
        type={'primary'}
        size={'small'}
        onClick={() => onChange?.(value && value.length > 0 ? value : undefined)}>
        Apply
      </Button>
    </Space>
  );
};
